import { type AggRequest, type AggResponse, fetchAgg } from '../../../../common/utils/fetch-agg';
import { generateHash } from '../../../../utils/caching/common/key-generator';
import { promiseCache } from '../../../../utils/caching/persist-refresh';
import { operationName, quickFindRecommendedDocumentsQuery } from './query';
import {
	type QuickFindRecommendedDocumentsQueryType,
	type QuickFindRecommendedDocumentsQueryVariables,
} from './types';

export type QuickFindRecommendedDocumentsQueryProps = Pick<
	AggRequest<QuickFindRecommendedDocumentsQueryVariables>,
	'aggAbsoluteUrl' | 'variables' | 'headers'
>;

export type CachedQuickFindRecommendedDocumentsQueryProps =
	QuickFindRecommendedDocumentsQueryProps & {
		expiration?: number | undefined;
		onlyRefreshIfExpired?: boolean | undefined;
		returnExpired?: boolean | undefined;
	};

const QUICK_FIND_RECOMMENDED_DOCUMENTS_CACHE_KEY = 'quick-find-recommended-documents';
const FAILURE_EXPIRATION_MS = 5 * 1000; // 5 seconds for failure
const SUCCESS_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes for success

export const recommendedDocumentsQuery: ({
	variables,
	aggAbsoluteUrl,
	headers,
}: QuickFindRecommendedDocumentsQueryProps) => Promise<
	AggResponse<QuickFindRecommendedDocumentsQueryType>
> = ({ variables, aggAbsoluteUrl, headers }: QuickFindRecommendedDocumentsQueryProps) => {
	return fetchAgg<
		QuickFindRecommendedDocumentsQueryVariables,
		QuickFindRecommendedDocumentsQueryType
	>({
		variables,
		aggAbsoluteUrl,
		graphQuery: quickFindRecommendedDocumentsQuery(),
		operationName,
		headers,
	});
};

const {
	refreshOnAccessDecorator: refreshOnAccessDecoratorRecommendedDocuments,
	clear: clearRecommendedDocumentsCache,
} = promiseCache<AggResponse<QuickFindRecommendedDocumentsQueryType>>({
	name: QUICK_FIND_RECOMMENDED_DOCUMENTS_CACHE_KEY,
});

export const cachedRecommendedDocumentsQuery: ({
	variables,
	aggAbsoluteUrl,
	expiration,
	onlyRefreshIfExpired,
	returnExpired,
	headers,
}: CachedQuickFindRecommendedDocumentsQueryProps) =>
	| AggResponse<QuickFindRecommendedDocumentsQueryType>
	| Promise<AggResponse<QuickFindRecommendedDocumentsQueryType>> = ({
	variables,
	aggAbsoluteUrl,
	expiration,
	onlyRefreshIfExpired,
	returnExpired,
	headers,
}: CachedQuickFindRecommendedDocumentsQueryProps) => {
	const cacheKey = generateHash(
		`${QUICK_FIND_RECOMMENDED_DOCUMENTS_CACHE_KEY}|${JSON.stringify(variables)}`,
	).toString();

	const writeBackWithTTL = (
		value:
			| AggResponse<QuickFindRecommendedDocumentsQueryType>
			| Promise<AggResponse<QuickFindRecommendedDocumentsQueryType>>,
		ttl: number,
	) => {
		refreshOnAccessDecoratorRecommendedDocuments({
			key: cacheKey,
			supplier: async () => value,
			returnExpired: false,
			onlyRefreshIfExpired: false,
			expiration: ttl,
		});
	};

	return refreshOnAccessDecoratorRecommendedDocuments({
		key: cacheKey,
		supplier: async () => {
			try {
				const result = await recommendedDocumentsQuery({ variables, aggAbsoluteUrl, headers });
				writeBackWithTTL(result, SUCCESS_EXPIRATION_MS);
				return result;
			} catch (error) {
				// On failure, cache the rejected promise with a short TTL to avoid hammering the service
				// This ensures the error is still thrown when accessed, but prevents immediate retries
				writeBackWithTTL(Promise.reject(error), FAILURE_EXPIRATION_MS);

				// Re-throw the error so the caller knows the request failed
				throw error;
			}
		},
		expiration,
		onlyRefreshIfExpired,
		returnExpired,
	});
};

export const __test_clearRecommendedDocumentsCache: (key?: string) => void = (key?: string): void =>
	clearRecommendedDocumentsCache(key);
